import merge from "lodash/merge";
import { BaselineData, BaselineSeriesOptions, ChartCandle } from "../chart.typings";
import BaseChartSeries from "./baseSeries";
import { BaseLineSeries as CustomBaselineSeries } from "../customSeries/baselineSeries/baselineSeries";
import { BaselineLastPricePulsePrimitive } from '../customSeries/baselineSeries/lastPricePulsePrimitive'
import { defaultOptions } from '../customSeries/baselineSeries/options'
import { IndicatorOutput_OutputStyle } from "../../../proto/generated/indicator";
import { evaluateExpression } from "../../../util/safeExpressionEval";
import { getViewportCandleInfo } from "../../../util/chart";
import { DeepPartial, ISeriesApi, Time } from "lightweight-charts";

class BaselineSeries extends BaseChartSeries<BaselineData, BaselineSeriesOptions> {

    customPaneView?: CustomBaselineSeries<BaselineData>

    init() {
        this.customPaneView = new CustomBaselineSeries(this.chart);
    }

    addSeriesToChart() {
        const seriesOptions = this.getDefaultOptions()
        if (this.chart.screenerChart) {
            merge(seriesOptions, { visible: false })
        }
        const api = this.chart.chartApi.addCustomSeries(this.customPaneView!, seriesOptions)
        this.customPaneView?.setSeriesApi(api as ISeriesApi<'Custom', Time, BaselineData>)
        api.attachPrimitive(new BaselineLastPricePulsePrimitive(this.chart, this.paneIndex))
        return api
    }
    
    getDefaultOptions(): BaselineSeriesOptions {
        const t = Number(this.config.thickness) || 1
        return merge(defaultOptions, this.options, {
            lineWidth: Math.max(2, Math.round(t * 2))
        })
    }

    onOptionsUpdate(options: DeepPartial<BaselineSeriesOptions>): void {
        if ('source' in options) {
            this._setData(this.rawData, this.metadata)
        }
    }

    /** First visible candle in the current viewport (not index 0 of loaded history). */
    getScreenerViewportBaseValue(data?: ChartCandle[], requireLiveViewport = false): number | undefined {
        const rawData = data ?? this.rawData
        if (!rawData.length) return undefined

        if (this.chart.chartApi) {
            const range = this.chart.chartApi.timeScale().getVisibleLogicalRange()
            if (range) {
                const barsInfo = this.seriesApi.barsInLogicalRange(range)
                if (barsInfo) {
                    const { rangeStart } = getViewportCandleInfo(range, barsInfo, rawData)
                    const startIndex = Math.max(0, Math.min(Math.floor(rangeStart), rawData.length - 1))
                    const close = rawData[startIndex]?.close
                    if (close !== undefined) return close
                }
            }
        }

        if (requireLiveViewport) return undefined

        const planned = this.chart.getPlannedViewportLogicalRange(rawData)
        if (!planned) return undefined
        const startIndex = Math.max(0, Math.min(Math.floor(planned.from), rawData.length - 1))
        const close = rawData[startIndex]?.close
        if (close === undefined) return undefined
        return close
    }

    applyScreenerBaseValue(data?: ChartCandle[], reveal = false) {
        if (!this.chart.screenerChart) return
        let baseValue = this.getScreenerViewportBaseValue(data, reveal)
        if (baseValue === undefined && reveal) {
            baseValue = this.getScreenerViewportBaseValue(data, false)
        }
        if (baseValue === undefined) return
        const current = this.seriesApi.options() as BaselineSeriesOptions & { baseValue?: number; visible?: boolean }
        const nextVisible = reveal ? true : false
        if (current.baseValue === baseValue && current.visible === nextVisible) return
        this.seriesApi.applyOptions({ baseValue, visible: nextVisible } as any)
    }

    updateScreenerBaseValue() {
        this.applyScreenerBaseValue(undefined, true)
    }

    _setData(data: ChartCandle[], metadata?: Record<string, any>) {
        this.metadata = metadata || {};
        this.setData(data, metadata)
        if (this.chart.screenerChart) {
            this.seriesApi.applyOptions({ visible: false } as any)
        }
        this.seriesApi.setData(this.data)
    }

    generateSeriesData(data: ChartCandle[], metadata: Record<string, any> = {}, update = false): BaselineData[] {
        const result: BaselineData[] = [];
        const open: number[] = new Array(data.length);
        const close: number[] = new Array(data.length);
        const series: number[] = new Array(data.length);

        const {color: seriesColor} = this.seriesApi.options()

        const {color: metaColor, lineStyle } = metadata;
        const {color: configColor} = this.config;
        const isColorHexCode = metaColor && RegExp(/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/).test(metaColor)
        const color = isColorHexCode ? configColor : metaColor;
        const source = this.seriesOptions.source

        if (isColorHexCode && seriesColor !== color) {
            this.seriesApi.applyOptions({color})
        }

        data.forEach((candle, i) => {
            if (candle[source] === undefined) return;
            open[i] = candle.open;
            close[i] = candle.close;
            series[i] = candle.series || 0
            let candleColor = color;
            if (isNaN(candle[source] as number) && this.config.style === IndicatorOutput_OutputStyle.WITH_BREAKS) {
                candleColor = "transparent"
            } else if (!isColorHexCode && candleColor) {
                candleColor = evaluateExpression<string>(metaColor, { open, close, series, candle, i })
            }

            result.push({
                ...candle,
                value: candle[source] as number
            })

        })

        return result;
    }

}

export default BaselineSeries;
